import type { FormikTouched } from 'formik';
import { getIn, useFormikContext } from 'formik';

/**
 * Utility function to determine if a field error should be shown.
 * Shows error if the field is touched OR if the form has been submitted at least once.
 *
 * Use this for class components or components wrapped with connect() HOC.
 */
export const shouldShowFieldError = (id: string, touched: FormikTouched<unknown>, submitCount: number): boolean =>
  // Use !! to coerce to boolean - getIn can return arrays/objects for nested fields
  !!getIn(touched, id) || submitCount > 0;

/**
 * Hook for functional components to get field error display state.
 * Uses useFormikContext() internally.
 */
export const useFormikFieldError = (id: string) => {
  const { touched, errors, submitCount } = useFormikContext();
  const showError = shouldShowFieldError(id, touched, submitCount);
  const errorMessage = getIn(errors, id) as string | undefined;
  return {
    showError,
    error: showError ? errorMessage : undefined,
    hasError: !!(showError && errorMessage),
  };
};
